import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import { Select } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import { COREAPP_USER_ROLES } from "../context/UserContext";
import { map } from "lodash";

const Joi = require("joi");

const emailSchema = Joi.string().email({ tlds: { allow: false } });

const useStyles = makeStyles(() => ({
  actionButtons: {
    paddingRight: "0.5",
    display: "flex",
    justifyContent: "right",
    width: "100%",
  },
  buttonStyle: {
    textTransform: "none",
  },
}));

const statusOptions = ["Active", "Inactive", "Suspended"];

const newUserStatusOptions = ["Inactive", "Suspended"];

const fieldsNeedValidate = new Set(["email", "status", "role"]);

const defaultUserInformation = {
  id: -1,
  email: "",
  firstName: "",
  lastName: "",
  status: "",
  role: "",
};

const defaultErrorText = {
  email: "",
  status: "",
  role: "",
};

export default function ManageUserPopup(props) {
  const classes = useStyles();
  const [values, setValues] = useState(defaultUserInformation);
  const [errors, setErrors] = useState({ ...defaultErrorText });
  const [originalValues, setOriginalValues] = useState(defaultUserInformation);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    if (fieldsNeedValidate.has(name)) {
      validate({ [name]: value });
    }
  };

  const resetForm = () => {
    setValues(defaultUserInformation);
    setErrors({ ...defaultErrorText });
  };

  const { addOrEdit, recordForEdit } = props;

  const getStatusOptions = () => {
    if (originalValues.id !== -1) {
      if (originalValues.status === "Invited") {
        return newUserStatusOptions;
      }
      return statusOptions;
    }
    return ["Invited"];
  };

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ("email" in fieldValues) {
      temp.email = emailSchema.validate(fieldValues.email).error
        ? "Email is not valid."
        : "";
    }
    if ("status" in fieldValues) {
      temp.status =
        fieldValues.status.length != 0 ? "" : "This field is required.";
    }
    if ("role" in fieldValues) {
      temp.role = fieldValues.role.length != 0 ? "" : "This field is required.";
    }
    setErrors({
      ...temp,
    });

    if (fieldValues == values) {
      if (fieldValues.id === -1) {
        return temp.email === "" && temp.role === "";
      }
      return Object.values(temp).every((x) => x == "");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      addOrEdit(values, resetForm);
    }
  };

  useEffect(() => {
    if (recordForEdit != null) {
      setValues({
        ...recordForEdit,
      });
      setOriginalValues({ ...recordForEdit });
    }
  }, [recordForEdit]);

  return (
    <div>
      <Box component="form" autoComplete="off" onSubmit={handleSubmit}>
        <Box
          sx={{
            "& > :not(style)": { m: 1, width: "250px" },
          }}
        >
          <TextField
            name="email"
            label="Email"
            value={values.email}
            onChange={handleInputChange}
            size="small"
            variant="standard"
            required
            error={errors.email !== ""}
            helperText={errors.email}
            disabled={values.id !== -1}
          />

          {values.id !== -1 && (
            <TextField
              name="firstName"
              label="First Name"
              value={values.firstName}
              onChange={handleInputChange}
              size="small"
              variant="standard"
              disabled
            />
          )}

          {values.id !== -1 && (
            <TextField
              name="lastName"
              label="Last Name"
              value={values.lastName}
              onChange={handleInputChange}
              size="small"
              variant="standard"
              disabled
            />
          )}

          {values.id !== -1 && (
            <FormControl
              variant="standard"
              required={values.id !== -1}
              sx={{ m: 1, minWidth: 120 }}
              error={errors.status !== ""}
            >
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={values.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="">None</MenuItem>
                {getStatusOptions(values.id).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
              {errors.status !== "" && (
                <FormHelperText>{errors.status}</FormHelperText>
              )}
            </FormControl>
          )}

          <FormControl
            variant="standard"
            required
            sx={{ m: 1, minWidth: 120 }}
            error={errors.role !== ""}
          >
            <InputLabel>Role</InputLabel>
            <Select
              value={values.role}
              onChange={handleInputChange}
              label="Role"
              name="role"
            >
              <MenuItem value="">None</MenuItem>
              {map(COREAPP_USER_ROLES, (role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
            {errors.role !== "" && (
              <FormHelperText>{errors.role}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <div className={classes.actionButtons}>
          {values.id === -1 && (
            <Button onClick={resetForm} color="primary" variant="text">
              Reset
            </Button>
          )}
          <Button
            className={classes.buttonStyle}
            type="submit"
            color="primary"
            variant="contained"
          >
            Submit
          </Button>
        </div>
      </Box>
    </div>
  );
}
